// Airtable Email Service for capturing signup emails
// This service handles email capture for both free and paid tier signups

const AIRTABLE_API_KEY = 'pat4FML0uvc5EtZiq.4b9c4db63db5d87249782d8456c6341da6a25cc41041487340138cd1d4453a99';
const AIRTABLE_BASE_ID = 'appEp9ztiYve51qqI';

// Table IDs from the API documentation
const FREE_SIGNUP_TABLE_ID = 'tblcIqWvuR1MrzOZZ';
const PAID_SIGNUP_TABLE_ID = 'tblPbxKtO1SSTdlDg';

// Field IDs from the API documentation
const FREE_SIGNUP_EMAIL_FIELD = 'fldOU8JUweRrltuz8';
const FREE_SIGNUP_DATE_FIELD = 'fldDvsXJ3dnOuUdvG';
const PAID_SIGNUP_NAME_FIELD = 'fld7cWREOvIMjTmtp'; // Using email as name
const PAID_SIGNUP_DATE_FIELD = 'fldL9D2c6NJ2lvJy1';

class AirtableEmailService {
  /**
   * Capture email for free tier signup
   * @param {string} email - User's email address
   * @returns {Promise<Object>} - Airtable response
   */
  async captureFreeSignup(email) {
    if (!email) {
      throw new Error('Email is required');
    }

    try {
      const response = await fetch(
        `https://api.airtable.com/v0/${AIRTABLE_BASE_ID}/${FREE_SIGNUP_TABLE_ID}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${AIRTABLE_API_KEY}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            records: [
              {
                fields: {
                  [FREE_SIGNUP_EMAIL_FIELD]: email,
                  [FREE_SIGNUP_DATE_FIELD]: new Date().toISOString()
                }
              }
            ]
          })
        }
      );

      const data = await response.json();
      
      if (!response.ok) {
        console.error('Airtable Error (Free Signup):', data);
        throw new Error(data.error?.message || 'Failed to capture free signup email');
      }

      console.log('Free signup email captured successfully:', email);
      return data;
    } catch (error) {
      console.error('Error capturing free signup email:', error);
      throw error;
    }
  }

  /**
   * Capture email for paid tier signup
   * @param {string} email - User's email address
   * @returns {Promise<Object>} - Airtable response
   */
  async capturePaidSignup(email) {
    if (!email) {
      throw new Error('Email is required');
    }

    try {
      const response = await fetch(
        `https://api.airtable.com/v0/${AIRTABLE_BASE_ID}/${PAID_SIGNUP_TABLE_ID}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${AIRTABLE_API_KEY}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            records: [
              {
                fields: {
                  [PAID_SIGNUP_NAME_FIELD]: email, // Using email in the Name field
                  [PAID_SIGNUP_DATE_FIELD]: new Date().toISOString()
                }
              }
            ]
          })
        }
      );

      const data = await response.json();
      
      if (!response.ok) {
        console.error('Airtable Error (Paid Signup):', data);
        throw new Error(data.error?.message || 'Failed to capture paid signup email');
      }

      console.log('Paid signup email captured successfully:', email);
      return data;
    } catch (error) {
      console.error('Error capturing paid signup email:', error);
      throw error;
    }
  }

  /**
   * Check if email already exists in free signup table
   * @param {string} email - Email to check
   * @returns {Promise<boolean>} - True if email exists
   */
  async checkFreeSignupExists(email) {
    try {
      const encodedFormula = encodeURIComponent(`{Email} = '${email}'`);
      const response = await fetch(
        `https://api.airtable.com/v0/${AIRTABLE_BASE_ID}/${FREE_SIGNUP_TABLE_ID}?filterByFormula=${encodedFormula}`,
        {
          headers: {
            'Authorization': `Bearer ${AIRTABLE_API_KEY}`,
          }
        }
      );

      const data = await response.json();
      return data.records && data.records.length > 0;
    } catch (error) {
      console.error('Error checking free signup existence:', error);
      return false;
    }
  }

  /**
   * Check if email already exists in paid signup table
   * @param {string} email - Email to check
   * @returns {Promise<boolean>} - True if email exists
   */
  async checkPaidSignupExists(email) {
    try {
      const encodedFormula = encodeURIComponent(`{Name} = '${email}'`);
      const response = await fetch(
        `https://api.airtable.com/v0/${AIRTABLE_BASE_ID}/${PAID_SIGNUP_TABLE_ID}?filterByFormula=${encodedFormula}`,
        {
          headers: {
            'Authorization': `Bearer ${AIRTABLE_API_KEY}`,
          }
        }
      );

      const data = await response.json();
      return data.records && data.records.length > 0;
    } catch (error) {
      console.error('Error checking paid signup existence:', error);
      return false;
    }
  }
}

// Export a singleton instance
export default new AirtableEmailService(); 